/**
 * Bulk-add API client (Block 6.E / ADR-017 §"Bulk-adding 12 organizations").
 *
 * Two-step flow:
 *   - preview: parse paste + duplicate-detection + pending-lookup-adds (no writes)
 *   - commit: atomic single-trx commit of all three blocks
 *
 * F11 enforcement: server requires `approved_lookup_seeding=true` if
 * `pending_lookup_adds.length > 0`. Mirrors substrate's
 * `--approve-lookup-seeding` flag per ADR-014 §T1.
 */

import { writeFetch } from './writeFetch';
import type {
  BulkAddCommitRequest,
  BulkAddCommitResponse,
  BulkAddPreviewResponse,
} from './types';
import { apiErrorFrom } from './api';

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function postJson<T>(
  slug: string,
  path: string,
  body: object,
  signal?: AbortSignal,
): Promise<T> {
  const r = await writeFetch(tenantUrl(slug, path), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!r.ok) throw await apiErrorFrom(r, `POST ${path}`);
  return (await r.json()) as T;
}

export const bulkAddApi = {
  preview(
    slug: string,
    paste: string,
    signal?: AbortSignal,
  ): Promise<BulkAddPreviewResponse> {
    return postJson(slug, '/providers/bulk-add/preview', { paste }, signal);
  },

  commit(
    slug: string,
    request: BulkAddCommitRequest,
    signal?: AbortSignal,
  ): Promise<BulkAddCommitResponse> {
    return postJson(slug, '/providers/bulk-add', request, signal);
  },
};
